import { AI_DOMAIN } from "@launchos/shared-utils/env";
import { getSessionToken } from "./auth";
import { createCampaignAndObject, getCampaign, getObjects, updateObject } from "./campaigns";
import { head } from "lodash";
import { Message } from "ai/react/dist";
import pako from "pako";
import { GET_CAMPAIGN } from "./gql/campaigns";
import { client } from "@/client";

interface CreateOrUpdateChatParams {
  // An array of all previous messages in the chat
  messages?: Message[];

  // The most recent message to add to the chat (usually the output of an onFinish method from a useChat hook)
  lastMessage?: Message;

  // The user's input that triggered the last message
  input?: string;

  // The unique identifier for the chat session (in the case of a session that will be updated)
  sessionId?: string;

  // The name of the chat session (optional)
  name?: string;

  // Whether to append the last message to the messages array
  noAppend?: boolean;

  // The state of the chat session
  // graphState?: any;

  // The thread of the chat session
  thread?: any;

  // The thread ID of the chat session
  threadId?: string;
}

export const compressData = (data: any) => {
  const compressed = pako.gzip(data);
  // Convert Uint8Array to base64 string for compact storage
  // Process in chunks to avoid "Maximum call stack size exceeded"
  let binary = '';
  const chunkSize = 8192; // Process 8KB at a time
  for (let i = 0; i < compressed.length; i += chunkSize) {
    const chunk = compressed.slice(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

export const decompressData = (base64Data: string) => {
  // Convert base64 back to Uint8Array
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return pako.ungzip(bytes, { to: 'string' });
}

export const complete = async (
  prompt = "Hi!",
  systemPrompt = "",
  type = null
) => {
  try {
    const r = await fetch(`${AI_DOMAIN}/api/completion`, {
      method: "post",
      body: JSON.stringify({ prompt, systemPrompt, type }),
    });
    const response = await r.json();
    return response.text;
  } catch (error) {
    console.error(`Error`, error);
    return false;
  }
};

export const completeComponent = async (
  prompt = "Give me an alternative version of this component",
  systemPrompt = "",
  settings = {},
  context = [],
  pageContent = []
) => {
  try {
    const r = await fetch(`${AI_DOMAIN}/api/copilot/artifacts/page/update`, {
      method: "post",
      body: JSON.stringify({
        prompt,
        systemPrompt,
        settings,
        context,
        pageContent,
      }),
    });
    const response = await r.json();
    return response;
  } catch (error) {
    console.error(`Error`, error);
    return false;
  }
};

export const createOrUpdateChat = async ({
  lastMessage,
  messages = [],
  input = "",
  sessionId,
  // graphState = {},
  threadId,
  thread = null,
  name,
  noAppend = false,
}: CreateOrUpdateChatParams) => {
  // console.log("createOrUpdateChat (incoming messages)", messages, {
  //   noAppend,
  //   threadId,
  //   thread,
  // });

  // if noAppend is true, don't append the last message to the messages array
  const allMessages = noAppend
    ? messages
    : [
        ...messages,
        {
          role: "user",
          id: Math.random().toString(36).substr(2, 9),
          content: input,
          createdAt: new Date().toISOString(),
        },
        lastMessage,
      ];

  // let storableThread = null;
  // if (thread) {
  //   // The 'client' property contains a live API client which is not serializable.
  //   // The 'messages' property is already being stored separately in `allMessages`.
  //   const {
  //     client,
  //     messages: _threadMessages,
  //     isLoading: _isLoading,
  //     branch: _branch,
  //     ...rest
  //   } = thread as any;
  //   storableThread = rest;
  //   // console.log("createOrUpdateChat - storableThread", storableThread);
  //   // console.log("createOrUpdateChat - storableThread.history", storableThread?.history);

  //   storableThread = compressData(JSON.stringify(storableThread));
  //   // console.log("createOrUpdateChat - storableThread compressed", storableThread);
  // }

  if (!Boolean(messages.length) || !sessionId) {
    // if no messages or no sessionId, create a new chat
    console.log("Will Create New Chat around this", lastMessage);
    const token = getSessionToken();

    const settings = {
      // messages: allMessages,
      // graphState,
      threadId,
      // thread: storableThread,
    };

    const useThisName =
      name ||
      (await complete(
        `Please generate a simple word or phrase (no more than 4 words) 
        to title the attached thread of chatbot messages.  

        Just return the phrase only.  

        Be unique and not generic.

        No before or after messages...just the word or phrase.

        The thread of messages is: 
        <thread>
        ${JSON.stringify(allMessages)}
        </thread>
        `
      ));

    const { campaign, object } = await createCampaignAndObject({
      token,
      name: useThisName,
      type: "chat",
      nodeType: "chat_history",
      settings,
    });

    console.log("Chat Campaign Created", campaign, object);
    return campaign;
  } else {
    console.log("Will Simply Update the current chat", lastMessage, messages, {
      threadId,
      thread,
    });

    const response = await getObjects(sessionId);
    const object = head(response?.data?.objects) as
      | { id: string; settings: any }
      | undefined;
    await updateObject(object?.id, {
      settings: {
        ...object?.settings,
        // messages: allMessages,
        // graphState,
        threadId,
        // thread: storableThread,
      },
    });
    return null;
  }
};

export const getChat = async (id: string) => {
  // const token = getSessionToken();
  
  // doesn't require a token
  const campaignResponse = await client.query({
    query: GET_CAMPAIGN,
    variables: { id },
  });

  const data = campaignResponse?.data?.campaign;

  console.log("getChat", data);

  // const data = await getCampaign({ token, id });
  const response = await getObjects(id);
  const object = head(response?.data?.objects) as { settings?: any } | undefined;
  const { settings = {} } = object || {};
  // const { thread = null } = settings;
  
  // if (!thread) {
    return {
      ...data,
      ...settings,
      // thread: null,
    }
  // }
  
  // const decompressedThread = decompressData(thread);
  // const parsedThread = JSON.parse(decompressedThread);
  
  // return {
  //   ...data,
  //   ...settings,
  //   // thread: parsedThread,
  // }
};
